import { Action, ActionType } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { iif, Observable, of, OperatorFunction, throwError, from } from 'rxjs';
import { catchError, concatMap, delay, map, mergeMap, retryWhen, switchMap, withLatestFrom } from 'rxjs/operators';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { fromApiRequest } from '@shared/apiClient/fromApiRequest';
import { toReduxError } from '@universal/errors';
import {
  GetProductsResponse,
  PostActivationOrderResponse,
  PostPaymentOrderResponse,
  PostProductUpdateResponse,
} from '@dtp/membership-service-types';
import { CustomerInformationResponse } from '@appTypes/customerInformationType';
import { Actions, actions } from '@redux/modules/myMembership';
import { TypedEpic } from './types';

// Epics
const purchaseProductEpic: TypedEpic = (action$: Observable<Action<any>>, state$) => {
  const errorArray = [400, 422];

  return action$.pipe(
    ofType(Actions.PURCHASE_PRODUCT),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.productActivation.request>[]
    >,
    mergeMap(([action]) =>
      from(
        apiClient.post<PostPaymentOrderResponse>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].v2.productsPayment,
          data: action.payload,
          withCredentials: true,
        }),
      ).pipe(
        map((response) => actions.productPurchase.success(response.data)),
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              if (!errorArray.includes(error.status)) {
                return of(error);
              }
              return throwError(error);
            }),
            // Use concat map to keep the errors in order and make sure they
            // aren't executed in parallel
            concatMap((e, i) =>
              // Executes a conditional Observable depending on the result
              // of the first argument
              iif(
                () => i > 10,
                // If the condition is true we throw the error (the last error)
                throwError(e),
                // Otherwise we pipe this back into our stream and delay the retry
                of(e).pipe(delay(500)),
              ),
            ),
          ),
        ),
        catchError((res) => of(actions.productPurchase.failure(res.response))),
      ),
    ),
  );
};

const activateProductEpic: TypedEpic = (action$: Observable<Action<any>>, state$) => {
  const errorArray = [400, 422];

  return action$.pipe(
    ofType(Actions.ACTIVATE_PRODUCT),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.productActivation.request>[]
    >,
    mergeMap(([action]) =>
      from(
        apiClient.post<PostActivationOrderResponse>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].v2.productsActivation,
          data: action.payload,
          withCredentials: true,
        }),
      ).pipe(
        map((response) => actions.productActivation.success(response.data)),
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              if (!errorArray.includes(error.status)) {
                return of(error);
              }
              return throwError(error);
            }),
            // Use concat map to keep the errors in order and make sure they
            // aren't executed in parallel
            concatMap((e, i) =>
              // Executes a conditional Observable depending on the result
              // of the first argument
              iif(
                () => i > 10,
                // If the condition is true we throw the error (the last error)
                throwError(e),
                // Otherwise we pipe this back into our stream and delay the retry
                of(e).pipe(delay(500)),
              ),
            ),
          ),
        ),
        catchError((res) => of(actions.productActivation.failure(res.response))),
      ),
    ),
  );
};

export const updateProductEpic: TypedEpic = (action$: Observable<Action<any>>, state$) => {
  const errorArray = [400, 422];

  return action$.pipe(
    ofType(Actions.UPDATE_PRODUCT),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.productUpdate.request>[]
    >,
    mergeMap(([action]) =>
      from(
        apiClient.post<PostProductUpdateResponse>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].v2.productsUpdate,
          data: action.payload,
          withCredentials: true,
        }),
      ).pipe(
        map((response) => actions.productUpdate.success(response.data, { request: action.payload })),
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              if (!errorArray.includes(error.status)) {
                return of(error);
              }
              return throwError(error);
            }),
            // Use concat map to keep the errors in order and make sure they
            // aren't executed in parallel
            concatMap((e, i) =>
              // Executes a conditional Observable depending on the result
              // of the first argument
              iif(
                () => i > 10,
                // If the condition is true we throw the error (the last error)
                throwError(e),
                // Otherwise we pipe this back into our stream and delay the retry
                of(e).pipe(delay(500)),
              ),
            ),
          ),
        ),
        catchError((res) => of(actions.productUpdate.failure(res.response, { request: action.payload }))),
      ),
    ),
  );
};

export const getMyMembershipEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_MY_MEMBERSHIP),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getMyMembership.request>[]
    >,
    switchMap(([action]) => {
      const params: Record<string, any> = { mittNaf: 'true' };

      if (action.meta.campaignCode) {
        params.campaignCode = action.meta.campaignCode;
      }

      return fromApiRequest((signal) =>
        apiClient.get<GetProductsResponse>(ApiType.apimMembershipApi, {
          signal,
          url: ApiPaths[ApiType.apimMembershipApi].v2.products,
          params,
        }),
      ).pipe(
        map((response) => actions.getMyMembership.success(response.data)),
        catchError((err) =>
          of(
            actions.getMyMembership.failure(
              toReduxError(
                err,
                'Oops, vi har problemer med motoren... Kunne ikke hente medlemskapet! Ta kontakt med kundesenteret hvis problemet fortsetter!',
              ),
            ),
          ),
        ),
      );
    }),
  );

export const getCustomerInformationEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_CUSTOMER_INFORMATION),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getCustomerInformation.request>[]
    >,
    switchMap(() =>
      fromApiRequest((signal) =>
        apiClient.get<CustomerInformationResponse>(ApiType.apimMembershipApi, {
          signal,
          url: ApiPaths[ApiType.apimMembershipApi].membership,
        }),
      ).pipe(
        map((response) => actions.getCustomerInformation.success(response.data)),
        catchError((err) =>
          of(
            actions.getCustomerInformation.failure(
              toReduxError(
                err,
                'Oops, vi har problemer med motoren... Kunne ikke hente kundeinformasjon! Ta kontakt med kundesenteret hvis problemet fortsetter!',
              ),
            ),
          ),
        ),
      ),
    ),
  );

export const epics: TypedEpic[] = [
  getMyMembershipEpic,
  purchaseProductEpic,
  activateProductEpic,
  updateProductEpic,
  getCustomerInformationEpic,
];
